import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { GoogleRecaptchaComponent } from '../../../../shared/components/google-recaptcha/google-recaptcha.component';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { takeUntil } from 'rxjs';
import { SelfSignupService } from '../../services/self-signup.service';
import {
  ConnectionSelfSignup,
  ServiceRegion,
  OrgType,
} from '../../models/connection-self-signup';
import {
  City,
  CountiesAndCitiesData,
  County,
  State,
  Zipcode,
} from '../../../../shared/models/address';
import { MultiselectFilterComponent } from '../../../../shared/components/multiselect-filter/multiselect-filter.component';
import { AddressService } from '../../../../shared/services/address/address.service';
import { isNullOrUndefinedOrEmpty } from '../../../../shared/utils/utils';

interface FieldValidation {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  email?: boolean;
  pattern?: RegExp;
}

@Component({
  selector: 'app-connection-self-signup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    GoogleRecaptchaComponent,
    MultiselectFilterComponent,
  ],
  templateUrl: './connection-self-signup.component.html',
  styleUrl: './connection-self-signup.component.scss',
})
export class ConnectionSelfSignupComponent
  extends DestroyBase
  implements OnInit
{
  private route = inject(ActivatedRoute);
  private selfSignupService = inject(SelfSignupService);
  private cdr = inject(ChangeDetectorRef);
  private addressService = inject(AddressService);

  isSubmitting = false;
  errorMessage = '';
  captchaToken = '';
  showSuccessMessage = false;
  isLoginRedirect = false;

  formData = {
    organizationName: '',
    orgType: null as OrgType | null,
    orgTypeDesc: '',
    contactEmail: '',
    website: '',
    hasItSupport: null,
    applicationNotes: '',
  };

  // Data
  statesList: State[] = [];
  countiesList: County[] = [];
  citiesList: City[] = [];
  zipCodesList: Zipcode[] = [];

  // Selected values
  selectedStates: number[] = [];
  selectedCounties: number[] = [];
  selectedCities: number[] = [];
  selectedZipCodes: number[] = [];

  // Organization types
  orgTypes = [
    {
      value: OrgType.CommunityDevelopmentFinancialInstitution,
      label: 'Community Development Financial Institution',
    },
    { value: OrgType.SmallBusinessLoanFund, label: 'Small Business Loan Fund' },
    { value: OrgType.CreditUnion, label: 'Credit Union' },
    { value: OrgType.Bank, label: 'Bank' },
    {
      value: OrgType.TechnicalAssistanceProvider,
      label: 'Technical Assistance Provider',
    },
    {
      value: OrgType.ImpactFundOrVentureFund,
      label: 'Impact Fund or Venture Fund',
    },
    { value: OrgType.ChamberOfCommerce, label: 'Chamber of Commerce' },
    {
      value: OrgType.GovernmentOrQuasiGovernmentEntity,
      label: 'Government or Quasi-Government Entity',
    },
    { value: OrgType.Other, label: 'Other' },
  ];

  validationConfig: Record<string, FieldValidation> = {
    organizationName: {
      required: true,
      minLength: 2,
      maxLength: 100,
    },
    orgType: {
      required: true,
    },
    orgTypeDesc: {
      required: true,
      maxLength: 100,
    },
    contactEmail: {
      required: true,
      email: true,
      maxLength: 100,
    },
    website: {
      maxLength: 200,
      pattern: /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+(\/.*)?$/,
    },
    applicationNotes: {
      maxLength: 500,
    },
    hasItSupport: {
      required: true,
    },
  };
  touchedFields: Record<string, boolean> = {};

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.isLoginRedirect = params['is_login_redirect'] === 'true';
      });

    this.loadStates();
  }

  isFormValid(): boolean {
    return (
      !isNullOrUndefinedOrEmpty(this.formData.organizationName) &&
      this.formData.orgType !== null &&
      !isNullOrUndefinedOrEmpty(this.formData.contactEmail) &&
      (this.formData.hasItSupport === true ||
        this.formData.hasItSupport === false) &&
      this.selectedStates.length > 0 &&
      this.selectedCounties.length > 0 &&
      !isNullOrUndefinedOrEmpty(this.captchaToken)
    );
  }

  loadStates(): void {
    this.addressService
      .getStates()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.statesList = res;
        this.cdr.detectChanges();
      });
  }

  private filterValidSelections(
    selected: number[],
    source: { id: number }[],
  ): number[] {
    const validIds = new Set(source.map((item) => item.id));
    return selected.filter((id) => validIds.has(id));
  }

  getCountiesAndCities(): void {
    this.addressService
      .getCitiesAndCountiesByStateIds(this.selectedStates)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: CountiesAndCitiesData) => {
        this.countiesList = res.counties;
        this.citiesList = res.cities;
        this.selectedCounties = this.filterValidSelections(
          this.selectedCounties,
          this.countiesList,
        );
        this.selectedCities = this.filterValidSelections(
          this.selectedCities,
          this.citiesList,
        );
        if (this.selectedCities.length === 0) {
          this.zipCodesList = [];
          this.selectedZipCodes = [];
        }
        this.cdr.detectChanges();
      });
  }

  getZipCodes(): void {
    this.addressService
      .getZipcodesByCityIds(this.selectedCities)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: Zipcode[]) => {
        this.zipCodesList = res;
        this.selectedZipCodes = this.filterValidSelections(
          this.selectedZipCodes,
          this.zipCodesList,
        );
        this.cdr.detectChanges();
      });
  }

  addStates(states: string[]): void {
    this.selectedStates = states.map(Number);

    if (states.length > 0) {
      this.getCountiesAndCities();
    } else {
      this.countiesList = [];
      this.citiesList = [];
      this.selectedCounties = [];
      this.selectedCities = [];
    }
  }

  addCounties(counties: string[]): void {
    this.selectedCounties = counties.map(Number);
  }

  addCity(cities: string[]): void {
    this.selectedCities = cities.map(Number);

    if (cities.length > 0) {
      this.getZipCodes();
    } else {
      this.zipCodesList = [];
      this.selectedZipCodes = [];
    }
  }

  addZipCode(zipcodes: string[]): void {
    this.selectedZipCodes = zipcodes.map(Number);
  }

  isOtherOrgType(): boolean {
    return this.formData.orgType === OrgType.Other;
  }

  onOrgTypeChange(value: OrgType | null): void {
    if (value !== OrgType.Other) {
      this.formData.orgTypeDesc = '';
    }
  }

  onCaptchaResolved(token: string): void {
    this.captchaToken = token;
    this.cdr.detectChanges();
  }

  onSubmit(): void {
    if (
      !this.formData.organizationName ||
      !this.formData.orgType ||
      !this.formData.contactEmail
    ) {
      this.errorMessage = 'Please fill all required fields.';
      return;
    }

    if (!this.formData.hasItSupport && this.formData.hasItSupport !== false) {
      this.errorMessage = 'Please select IT support option.';
      return;
    }

    if (this.selectedStates.length === 0) {
      this.errorMessage = 'Please select at least one state.';
      return;
    }

    if (this.selectedCounties.length === 0) {
      this.errorMessage = 'Please select at least one county.';
      return;
    }

    if (!this.captchaToken) {
      this.errorMessage = 'Please complete reCAPTCHA.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const payload: Partial<ConnectionSelfSignup> = {
      organization_name: this.formData.organizationName,
      org_type: this.formData.orgType,
      org_type_desc: this.formData.orgTypeDesc || undefined,
      contact_email: this.formData.contactEmail,
      website: this.formData.website,
      service_region: this.buildServiceRegion(),
      application_notes: this.formData.applicationNotes || undefined,
      has_it_support: this.formData.hasItSupport,
      recaptcha: this.captchaToken,
    };

    this.selfSignupService
      .createConnectionSelfSignupApplication(payload as ConnectionSelfSignup)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.showSuccessMessage = true;
          this.isLoginRedirect = false;
        },
        error: (error) => {
          console.error('Error creating connection application:', error);
          this.isSubmitting = false;
          this.errorMessage = this.getErrorMessage(error);
        },
      });
  }

  private buildServiceRegion(): ServiceRegion {
    const stateSet = new Set(this.selectedStates.map(Number));
    const countySet = new Set(this.selectedCounties.map(Number));
    const citySet = new Set(this.selectedCities.map(Number));
    const zipSet = new Set(this.selectedZipCodes.map(Number));

    return {
      state: this.statesList
        .filter((s) => stateSet.has(s.id))
        .map((s) => s.name),

      county: this.countiesList
        .filter((c) => countySet.has(c.id))
        .map((c) => c.name),

      city: this.citiesList.filter((c) => citySet.has(c.id)).map((c) => c.name),

      zip_code: this.zipCodesList
        .filter((z) => zipSet.has(z.id))
        .map((z) => z.code),
    };
  }

  private getErrorMessage(error: HttpErrorResponse | unknown): string {
    const errObj = (error as HttpErrorResponse)?.error;
    if (errObj?.detail) {
      return errObj.detail;
    }
    if (errObj?.message) {
      return errObj.message;
    }
    if (
      Array.isArray(errObj) &&
      errObj.length > 0 &&
      typeof errObj[0] === 'string'
    ) {
      return errObj[0];
    }
    if (typeof errObj === 'string' && errObj) {
      return errObj;
    }
    return 'An error occurred while submitting your application. Please try again or contact support.';
  }

  onBlur(fieldName: string): void {
    this.touchedFields[fieldName] = true;
  }

  getFieldError(fieldName: string, label: string): string {
    const config = this.validationConfig[fieldName];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value = (this.formData as any)[fieldName];

    if (!config || !this.touchedFields[fieldName]) return '';

    // REQUIRED
    if (config.required && value === '') {
      return `${label} is required`;
    }

    // EMAIL
    if (config.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value)) {
        return 'Please enter a valid email address';
      }
    }

    // MIN LENGTH
    if (config.minLength && value && value.length < config.minLength) {
      return `${label} must be at least ${config.minLength} characters`;
    }

    // MAX LENGTH
    if (config.maxLength && value && value.length > config.maxLength) {
      return `${label} must not exceed ${config.maxLength} characters`;
    }

    // PATTERN (website)
    if (config.pattern && fieldName === 'website') {
      if (value && !config.pattern.test(value)) {
        return 'Please enter a valid website URL (e.g., example.com or https://example.com)';
      }
    }

    return '';
  }
}
